import { useEffect, useRef } from "react";

export default function CyberBackground() {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: null, y: null });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    let animationFrameId;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    // Track dark mode by checking classList on documentElement
    const isDark = () => document.documentElement.classList.contains("dark");

    // Handle resize
    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize);

    // Track mouse
    const handleMouseMove = (e) => {
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
    };
    const handleMouseLeave = () => {
      mouseRef.current.x = null;
      mouseRef.current.y = null;
    };
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseleave", handleMouseLeave);

    // Particle class definition
    const particles = [];
    const particleCount = Math.min(65, Math.floor((width * height) / 25000)); // Dynamic count based on screen size

    class Particle {
      constructor() {
        this.reset(true);
      }

      reset(init = false) {
        this.x = Math.random() * width;
        this.y = init ? Math.random() * height : -10;
        this.vx = (Math.random() - 0.5) * 0.4;
        this.vy = (Math.random() - 0.5) * 0.4 + 0.15; // Drift slightly downwards
        this.radius = Math.random() * 1.5 + 1;
        this.baseAlpha = Math.random() * 0.4 + 0.15;
        this.alpha = this.baseAlpha;
        this.pulseSpeed = Math.random() * 0.02 + 0.005;
        this.pulsePhase = Math.random() * Math.PI;
        // 15% chance to carry binary telemetry data
        this.telemetry = Math.random() < 0.15 ? (Math.random() < 0.5 ? "0" : "1") : null;
      }

      update(scanLineY) {
        this.x += this.vx;
        this.y += this.vy;

        // Wrap around boundaries
        if (this.x < 0 || this.x > width) this.vx *= -1;
        if (this.y > height + 10) {
          this.reset(false);
        }

        // Pulse effect
        this.pulsePhase += this.pulseSpeed;
        this.alpha = this.baseAlpha + Math.sin(this.pulsePhase) * 0.1;

        // Scanline highlight interaction
        const distanceToScanline = Math.abs(this.y - scanLineY);
        if (distanceToScanline < 80) {
          const factor = 1 - distanceToScanline / 80;
          this.alpha = Math.min(1, this.alpha + factor * 0.5);
          this.radiusModifier = factor * 1.5;
        } else {
          this.radiusModifier = 0;
        }
      }

      draw(dark) {
        const radius = this.radius + (this.radiusModifier || 0);
        ctx.beginPath();
        ctx.arc(this.x, this.y, radius, 0, Math.PI * 2);

        if (dark) {
          ctx.fillStyle = `rgba(16, 185, 129, ${this.alpha})`; // Emerald in dark mode
        } else {
          ctx.fillStyle = `rgba(10, 110, 69, ${this.alpha})`; // Forest emerald in light mode
        }
        ctx.fill();

        // Render binary telemetry values near the node
        if (this.telemetry && this.alpha > 0.3) {
          ctx.font = "8px monospace";
          if (dark) {
            ctx.fillStyle = `rgba(6, 182, 212, ${this.alpha - 0.15})`; // Cyan telemetry
          } else {
            ctx.fillStyle = `rgba(124, 58, 237, ${this.alpha - 0.15})`; // Purple telemetry
          }
          ctx.fillText(this.telemetry, this.x + 6, this.y + 3);
        }
      }
    }

    // Initialize particles
    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle());
    }

    // Scanline configuration
    let scanLineY = 0;
    const scanLineSpeed = 1.2;

    // Animation Loop
    const animate = () => {
      ctx.clearRect(0, 0, width, height);

      const dark = isDark();
      scanLineY = (scanLineY + scanLineSpeed) % height;

      // Draw faint scanline sweep
      ctx.beginPath();
      const gradient = ctx.createLinearGradient(0, scanLineY - 30, 0, scanLineY + 30);
      if (dark) {
        gradient.addColorStop(0, "rgba(16, 185, 129, 0)");
        gradient.addColorStop(0.5, "rgba(16, 185, 129, 0.02)");
        gradient.addColorStop(1, "rgba(16, 185, 129, 0)");
      } else {
        gradient.addColorStop(0, "rgba(10, 110, 69, 0)");
        gradient.addColorStop(0.5, "rgba(10, 110, 69, 0.015)");
        gradient.addColorStop(1, "rgba(10, 110, 69, 0)");
      }
      ctx.fillStyle = gradient;
      ctx.fillRect(0, scanLineY - 30, width, 60);

      // Update and draw particles
      particles.forEach((p) => {
        p.update(scanLineY);
        p.draw(dark);
      });

      // Draw interactive connections
      const maxDistance = 110;
      const mouse = mouseRef.current;

      for (let i = 0; i < particles.length; i++) {
        const p1 = particles[i];

        // Draw line to mouse
        if (mouse.x !== null && mouse.y !== null) {
          const dx = p1.x - mouse.x;
          const dy = p1.y - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 160) {
            const alpha = (1 - dist / 160) * 0.25;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(mouse.x, mouse.y);
            if (dark) {
              ctx.strokeStyle = `rgba(6, 182, 212, ${alpha})`; // Cyan user-connections in dark mode
            } else {
              ctx.strokeStyle = `rgba(10, 110, 69, ${alpha})`; // Emerald user-connections in light mode
            }
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }

        // Draw lines to other particles
        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < maxDistance) {
            const alpha = (1 - dist / maxDistance) * 0.15;
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            if (dark) {
              ctx.strokeStyle = `rgba(16, 185, 129, ${alpha})`;
            } else {
              ctx.strokeStyle = `rgba(148, 163, 184, ${alpha})`; // Faint grey lines in light mode
            }
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full pointer-events-none -z-10 block"
    />
  );
}
